<script setup>
import Icon from '@js/Components/Icon/Icon.vue'
import LoadingSpinner from '@js/Components/Forms/LoadingSpinner/LoadingSpinner.vue'
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'

const props = defineProps({
  tag: {
    type: String,
    default: 'button'
  },
  href: {
    type: String,
    default: ''
  },
  big: {
    type: Boolean,
    default: false
  },
  small: {
    type: Boolean,
    default: false
  },
  outline: {
    type: Boolean,
    default: false
  },
  color: {
    type: String,
    default: 'pink-500'
  },
  fullWidthOnMobile: {
    type: Boolean,
    default: false
  },
  fullWidthOnDesktop: {
    type: Boolean,
    default: false
  },
  iconRight: {
    type: [String, Boolean],
    default: ''
  },
  iconLeft: {
    type: [String, Boolean],
    default: ''
  },
  consistentIconSize: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // Shows an in-button spinner instead of iconRight and blocks further presses. Use it for
  // any handler that awaits, so the press changes the DOM in the same frame.
  loading: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: ''
  },
  target: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: ''
  },

  // Tag used by Dusk browser tests, added directly to the input element
  dusk: {
    type: [String, null],
    default: null
  }
})

// Busy and disabled behave alike — neither activates — but they must not look alike:
// the greyed-out skin reads as "you can't do this", which is the wrong signal to send
// someone who just pressed. Only `disabled` gets that skin; `loading` stays at full
// strength and shows the spinner.
const isInactive = computed(() => props.disabled || props.loading)
const isBusy = computed(() => props.loading && !props.disabled)
</script>
<template>
  <div
    :class="{
      block: fullWidthOnMobile,
      'inline-block': !fullWidthOnMobile,
      'lg:block': fullWidthOnDesktop,
      'lg:inline-block': !fullWidthOnDesktop
    }"
  >
    <component
      :is="tag === 'link' ? Link : tag"
      :id="id"
      :type="type"
      :href="isInactive || !href ? '#' : href"
      :dusk="dusk"
      :target="target"
      :disabled="tag === 'button' && loading ? true : null"
      :aria-busy="loading ? 'true' : null"
      class="p-3 rounded-md flex items-center justify-center transition-colors text-base font-medium"
      :class="{
        [`text-${color} hover:text-white hover:bg-${color} border border-${color}`]: outline && !isInactive,
        [`text-${color} border border-${color} cursor-wait`]: outline && isBusy,
        'cursor-wait': !outline && isBusy,
        [`text-${color}  border border-${color} opacity-30 cursor-not-allowed hover:bg-white hover:text-${color}`]:
          outline && disabled,
        [`text-${color}  border border-${color} opacity-30 cursor-not-allowed`]: !outline && disabled,
        [`text-white bg-${color} hover:brightness-90 transition-all border border-${color}`]: !outline,
        [`p-[15px]`]: big,
        [`p-[8px] font-normal`]: small,
        'w-full': fullWidthOnMobile,
        'lg:w-full': fullWidthOnDesktop,
        'lg:w-auto': !fullWidthOnDesktop
      }"
    >
      <Icon v-if="iconLeft" :name="iconLeft" :class="{ 'pr-2': $slots.default }" :icon-width="consistentIconSize ? 'w-5 h-5' : ''" />
      <span><slot /></span>
      <LoadingSpinner v-if="loading" :show-text="false" :extra-small="true" :class="{ 'pl-2': $slots.default }" />
      <Icon v-else-if="iconRight" :name="iconRight" class="relative top-[0px]" :class="{ 'pl-2': $slots.default }" :icon-width="consistentIconSize ? 'w-5 h-5' : ''" />
    </component>
  </div>
</template>
